import { ref } from 'vue'
import { defineStore } from 'pinia'
import { loadStripe } from '@stripe/stripe-js'

import appleUtils from '@/utils/apple-utils'

export const useStripeStore = defineStore('stripe', () => {
  const appleMerchantIdentifier = ref()
  const isApplePayAvailable = ref(false)
  const isGooglePayAvailable = ref(false)
  const isLoading = ref(true)
  const setupApplePay = ref(false)
  const stripe = ref()

  async function init() {
    let stripeOpts = {}

    if (import.meta.env.VITE_STRIPE_CHARGE_TYPE == 'direct') {
      stripeOpts = { stripeAccount: import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID }
    }

    stripe.value = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, stripeOpts)
    await setupAvailableWallets()
    appleMerchantIdentifier.value = import.meta.env.VITE_APPLE_MERCHANT_IDENTIFIER
    isLoading.value = false
  }

  async function setupAvailableWallets() {
    const paymentRequest = stripe.value.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Total', amount: 1 }
    })
    // Check the availability of the Payment Request API.
    const canMakePaymentResult = await paymentRequest.canMakePayment()
    if (canMakePaymentResult) {
      isApplePayAvailable.value = canMakePaymentResult.applePay
      isGooglePayAvailable.value = canMakePaymentResult.googlePay || !appleUtils.isAppleDevice
      setupApplePay.value = !canMakePaymentResult.applePay && appleUtils.existsAppleSession
    } else {
      setupApplePay.value = appleUtils.existsAppleSession
    }
  }

  async function requestWalletPaymentMethod(
    amount: number,
    onPaymentMethodCompleted: (event: any) => void,
    requestPayerEmail: boolean = true
  ) {
    const paymentRequestData: any = {
      country: 'US',
      currency: 'usd',
      total: { label: 'Order Total', amount: amount },
      requestPayerName: true,
      requestPayerEmail,
      requestPayerPhone: true
    }

    if (import.meta.env.VITE_STRIPE_CHARGE_TYPE != 'direct') {
      paymentRequestData['onBehalfOf'] = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID
    }

    const paymentRequest = stripe.value.paymentRequest(paymentRequestData)
    await paymentRequest.canMakePayment()
    paymentRequest.on('paymentmethod', onPaymentMethodCompleted)
    paymentRequest.show()
  }

  return {
    appleMerchantIdentifier,
    isApplePayAvailable,
    isGooglePayAvailable,
    init,
    isLoading,
    stripe,
    setupApplePay,
    setupAvailableWallets,
    requestWalletPaymentMethod
  }
})
